import { useState } from "react";
import { Alert, Box, Button, TextField, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import PartyAddressForm, { ADDRESS_KEYS } from "./PartyAddressForm";

// #3579 / #3603 / #3604 / #3605 (ADR 0004: a staff error says what is wrong,
// why, and gives the fix). report-api's 400 usef_validation_error carries
// structuredIssues[] coded `party_missing_usef_id` / `party_missing_address`
// (usef-serializer.js) naming the ride, the person and, for an address, the
// missing key. This is the ONE fix panel for a class's parties: each
// rider/owner/trainer with a problem gets one block offering every fix that
// applies (type the USEF ID, declare a Show Pass (EX) or FEI, add a mailing
// address). All of it rides back as partyOverrides[rideId][role] on the retry
// (useFederationResults.buildClassBody). A non-member may legally ride on a
// Show Pass (USEF Results Criteria item 5.b), which only staff know.

export const PARTY_ISSUE_CODES = new Set(["party_missing_usef_id", "party_missing_address"]);

// One entry per (ride, role), in first-seen order: whether the server says the
// person has no USEF ID, and which address keys it says are missing.
export function partiesFromStructuredIssues(structuredIssues) {
  const byKey = new Map();
  for (const issue of structuredIssues || []) {
    if (!PARTY_ISSUE_CODES.has(issue?.code) || !issue.rideId || !issue.party) continue;
    const key = `${issue.rideId}|${issue.party}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        rideId: issue.rideId,
        role: issue.party,
        name: issue.partyName,
        cno: issue.cno,
        horse: issue.horse,
        noUsefId: false,
        missingAddressKeys: new Set(),
      });
    }
    const entry = byKey.get(key);
    if (issue.code === "party_missing_usef_id") entry.noUsefId = true;
    else if (issue.addressKey) entry.missingAddressKeys.add(issue.addressKey);
  }
  return [...byKey.values()];
}

function roleLabel(t, role) {
  switch (role) {
    case "rider":
      return t("publishResults.partyRoleRider");
    case "owner":
      return t("publishResults.partyRoleOwner");
    default:
      return t("publishResults.partyRoleTrainer");
  }
}

// #3683: static keys (no dynamic i18n keys) for naming exactly what is missing.
function missingAddressWhat(t, keys) {
  if (keys.length === ADDRESS_KEYS.length) return t("publishResults.addressWhatAll");
  const label = {
    address: t("publishResults.addressWhatStreet"),
    city: t("publishResults.addressWhatCity"),
    state: t("publishResults.addressWhatState"),
    zip: t("publishResults.addressWhatZip"),
  };
  return keys.map((k) => label[k]).join(", ");
}

export default function PartyUsefIdOverrides({
  classId,
  structuredIssues,
  overrides,
  onChange,
  onClearReason,
  onSetAddress,
  onUndoAddress,
  onRetry,
  busy,
}) {
  const { t } = useTranslation("scoreadmin");
  const [openAddressForm, setOpenAddressForm] = useState(null);
  const parties = partiesFromStructuredIssues(structuredIssues);
  if (parties.length === 0) return null;

  return (
    <Alert severity="warning" sx={{ mb: 1 }} data-testid={`party-missing-usefid-alert-${classId}`}>
      {parties.map((p) => {
        const value = overrides?.[p.rideId]?.[p.role] || {};
        const role = roleLabel(t, p.role);
        const suffix = `${classId}-${p.rideId}-${p.role}`;
        const who = {
          role,
          name: p.name || t("publishResults.unknownRider"),
          cno: p.cno ?? "?",
          horse: p.horse || t("publishResults.unknownHorse"),
        };
        const needsAddress = p.missingAddressKeys.size > 0;
        const missingKeys = ADDRESS_KEYS.filter((k) => p.missingAddressKeys.has(k));
        const addressHeld = missingKeys.every((k) => value[k]);
        const reasonLabel =
          value.noIdReason === "EX"
            ? t("publishResults.showPassDeclared")
            : value.noIdReason === "FEI"
              ? t("publishResults.feiDeclared")
              : null;
        return (
          <Box key={p.key} sx={{ mb: 1.5 }} data-testid={`party-block-${suffix}`}>
            <Typography variant="body2" sx={{ mb: 0.5 }} data-testid={`party-message-${suffix}`}>
              {p.noUsefId
                ? t("publishResults.partyMissingUsefId", who)
                : t("publishResults.partyMissingAddress", { ...who, what: missingAddressWhat(t, missingKeys) })}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
              {p.noUsefId && (
                <TextField
                  size="small"
                  label={t("publishResults.partyUsefId", { role })}
                  value={value.usefId ?? ""}
                  onChange={(e) => onChange(p.rideId, p.role, { usefId: e.target.value })}
                  data-testid={`party-usefid-input-${suffix}`}
                />
              )}
              {p.noUsefId &&
                (reasonLabel ? (
                  <>
                    <Typography variant="body2" data-testid={`party-showpass-declared-${suffix}`}>
                      {reasonLabel}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => onClearReason(p.rideId, p.role)}
                      data-testid={`party-showpass-undo-${suffix}`}
                    >
                      {t("publishResults.showPassUndo")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onChange(p.rideId, p.role, { noIdReason: "EX" })}
                      data-testid={`party-showpass-button-${suffix}`}
                    >
                      {t("publishResults.showPassButton")}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onChange(p.rideId, p.role, { noIdReason: "FEI" })}
                      data-testid={`party-fei-button-${suffix}`}
                    >
                      {t("publishResults.feiButton")}
                    </Button>
                  </>
                ))}
              {needsAddress &&
                (addressHeld ? (
                  <>
                    <Typography variant="body2" data-testid={`party-address-set-${suffix}`}>
                      {t("publishResults.addressSet", {
                        address:
                          missingKeys.length === ADDRESS_KEYS.length
                            ? `${value.address}, ${value.city}, ${value.state} ${value.zip}`
                            : missingKeys.map((k) => value[k]).join(", "),
                      })}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => onUndoAddress(p.rideId, p.role)}
                      data-testid={`party-address-undo-${suffix}`}
                    >
                      {t("publishResults.showPassUndo")}
                    </Button>
                  </>
                ) : openAddressForm === p.key ? (
                  <Box sx={{ width: "100%" }}>
                    <PartyAddressForm
                      testIdSuffix={suffix}
                      keys={missingKeys}
                      onSave={(addr) => {
                        onSetAddress(p.rideId, p.role, addr);
                        setOpenAddressForm(null);
                      }}
                      onCancel={() => setOpenAddressForm(null)}
                    />
                  </Box>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setOpenAddressForm(p.key)}
                    data-testid={`party-address-button-${suffix}`}
                  >
                    {t("publishResults.addressAdd")}
                  </Button>
                ))}
            </Box>
            <Typography
              variant="caption"
              component="div"
              sx={{ mt: 0.5 }}
              data-testid={`party-source-note-${suffix}`}
            >
              {needsAddress ? t("publishResults.partyNoAddress") : t("publishResults.partyFvdSource")}
            </Typography>
          </Box>
        );
      })}
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={onRetry}
        data-testid={`party-override-retry-${classId}`}
      >
        {t("publishResults.retryUpload")}
      </Button>
    </Alert>
  );
}
